import { useMemo } from 'react';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import UserPlan from '@creditflowresearch/user-plan/UserPlan';
import { UsMarket, EuropeanMarket, ApacMarket, EmergingMarket, Market } from '@/creditflowresearch/domain/Market';

export interface IMarketFilter {
  id: string,
  abbreviation: string,
  queryName?: string,
  disabled?: boolean,
  tooltip?: string | React.ReactNode,
  restrictedTooltip?: string | React.ReactNode,
  metadata?: { isBeta?: boolean },
  marketDataName?: string,
}

const isMarketAccessible = (marketId: string, accessibleRegionsIds: string[]): boolean => {
  const market = Market.from(marketId);
  if (!market) return false;

  const region = market.belongsToRegion();
  if (!region) return false;

  return accessibleRegionsIds.includes(region.getId());
};

export const useCreateMarketFilters = (): IMarketFilter[] => {
  const { plan, regions: accessibleRegionsIds } = useUserPlanContext();

  return useMemo<IMarketFilter[]>(() => {
    return [
      {
        id: ApacMarket.MARKET_ID,
        abbreviation: ApacMarket.NAME,
        queryName: ApacMarket.ABBREVIATION,
        disabled: !isMarketAccessible(ApacMarket.MARKET_ID, accessibleRegionsIds)
                  || !UserPlan.isSatisfiedPremiumPlan(plan),
        tooltip: ApacMarket.NAME,
        marketDataName: ApacMarket.NAME,
      },
      {
        id: EmergingMarket.MARKET_ID,
        abbreviation: EmergingMarket.NAME,
        queryName: EmergingMarket.ABBREVIATION,
        disabled: !isMarketAccessible(EmergingMarket.MARKET_ID, accessibleRegionsIds)
                  || !UserPlan.isSatisfiedPremiumPlan(plan),
        tooltip: EmergingMarket.NAME,
        marketDataName: EmergingMarket.NAME,
      },
      {
        id: EuropeanMarket.MARKET_ID,
        abbreviation: EuropeanMarket.NAME,
        queryName: EuropeanMarket.ABBREVIATION,
        // TODO: remove when the European market opening period ends; restore the
        // isMarketAccessible and isSatisfiedPremiumPlan checks the siblings use
        disabled: false,
        tooltip: EuropeanMarket.NAME,
        marketDataName: EuropeanMarket.NAME,
      },
      {
        id: UsMarket.MARKET_ID,
        abbreviation: UsMarket.NAME,
        queryName: UsMarket.ABBREVIATION,
        disabled: !isMarketAccessible(UsMarket.MARKET_ID, accessibleRegionsIds)
                  || !UserPlan.isSatisfiedPremiumPlan(plan),
        tooltip: UsMarket.NAME,
        marketDataName: UsMarket.NAME,
      },
    ];
  }, [plan, accessibleRegionsIds]);
};
